/**
 * <clevel-funding-cta> — the article contribution block, as one custom element.
 *
 * What it is, in one line: a heading, four amounts and a button that sends the reader
 * straight to Stripe.
 *
 * WHERE IT GOES. Inside a consumer article — Train vs Plane, Flying vs Driving, flight
 * emissions — where somebody has just read what their journey costs the atmosphere and has
 * nowhere to put the feeling. It does not need the calculator and it does not link to it.
 *
 * THE RULES IT IS BUILT TO KEEP, and where they come from:
 *
 *   C-8   The browser is never trusted for a price and never sends one. This file sends a
 *         KEY — `10`, `25`, `100`, `500` or `other`. Search this file for a sum on money and
 *         there is none. The free box sends the visitor's own text, unread, and the worker
 *         decides what it means.
 *   W-1   A web component, not an iframe. The content belongs to the host page.
 *   W-2   The interactive part lives entirely in shadow DOM.
 *   W-3   It never writes a stylesheet into the host page's <head>. That is what made the
 *         old widget fight the website's design.
 *   W-6   The complete block is drawn synchronously, at its final size, before any network
 *         request. Nothing on the page moves afterwards.
 *   W-7   Themed with CSS custom properties.
 *
 * WHY THERE IS NO PAYMENT PANEL INSIDE THE BLOCK
 * ---------------------------------------------
 * The design handoff draws one, with Apple Pay, Google Pay and card buttons. Stripe's own
 * hosted page already offers Apple Pay and Google Pay when the browser supports them, so
 * building the panel would add a screen, add Stripe's script to every article, and change
 * nothing the reader can see. One button, straight out to Stripe. The reasoning is written
 * out in the worker's `checkout.ts`.
 *
 * WHAT IT NEVER SAYS
 * ------------------
 * Offset. Carbon neutral. Net zero. Cancelled. A number of tonnes. This route establishes
 * none of those and the copy must never imply one. The accuracy line under the button says
 * so out loud rather than leaving the reader to infer it.
 */

import {
  GENERIC_FAILURE,
  amountLabelFromReturn,
  buildDonateRequest,
  cameBackFromPayment,
  creditsLabelFromReturn,
  readAmounts,
  readDonateAnswer,
  sourcePageOf,
  type Amount,
} from './logic.js';

const DEFAULT_ENDPOINT = 'https://clevel-article-funding-staging.clevel-flight-quote-worker.workers.dev';

/** The free-amount button's key. Must match the worker's `CUSTOM_KEY`. */
const CUSTOM_KEY = 'other';

/**
 * The sentence beside the newsletter tick box.
 *
 * Word for word the same as `CONSENT_WORDING` in the worker's `aftercare.ts`, because the
 * worker records this sentence as the evidence of what the reader agreed to. If the two ever
 * differ, the evidence is a sentence nobody was shown.
 */
const DEFAULT_CONSENT =
  'Email me occasionally about C Level projects. Unsubscribe any time, in one click.';

/**
 * The block's own styles.
 *
 * Every measurement is from the design handoff of 22 August 2026. The two that carry the
 * most weight, because they were defects found in that document's own mobile testing:
 *
 *   - `minmax(min(280px,100%),1fr)` on the amount grid. Plain `minmax(280px,1fr)` refuses to
 *     shrink below its minimum and pushes past the card on a 320px phone.
 *   - `4cqi` for padding, not `4vw`. The block responds to its own width, so it is right in
 *     a sidebar, a narrow column or full width without further work. That is what
 *     `container-type:inline-size` on the host is for.
 */
const STYLES = `
:host{
  --fs-2:32px; --fs-3:20px; --fs-4:16px; --fs-5:13px;
  --w-r:400; --w-m:600; --w-b:700;
  --s-1:8px; --s-2:16px; --s-3:24px;
  --radius:2px; --stroke-1:1px; --stroke-2:1.5px; --stroke-3:2px;
  --control:48px; --control-lg:56px;
  --ink:#3E4454; --paper:#F4F8F9; --teal:#005A69; --lime:#CEF17B; --aqua:#00EED2;
  --alert:#9E3B2A;

  display:block;
  container-type:inline-size;

  /* THE BLOCK MUST NOT BE WIDER THAN THE WORDS AROUND IT.
     C Level's article template caps paragraphs at 680px but leaves anything else to fill the
     920px content area. So on 23 August 2026 this block went live 240px wider than the text it
     sat in, sharing the left edge and sticking out to the right. It read as broken, because it
     was. The article's own images do the same thing and always have — that one is the theme's
     behaviour on every article and is not this file's to fix.
     Overridable, because 680 is C Level's measure and not a law: set the CSS custom property
     --clevel-funding-cta-width on the element to put it in a wider or narrower column.
     (No backticks in this comment. They would end the template literal this stylesheet lives
     in, and the error you get points at a line some distance away.) */
  max-width:var(--clevel-funding-cta-width,680px);

  /* 64px of clearance above and below, as the handoff specifies for article context. */
  margin:64px 0;
  background:#fff;
  border:var(--stroke-1) solid rgba(62,68,84,.16);
  border-radius:var(--radius);
  color:var(--ink);
  /* Epilogue is already loaded by the site's theme. This file never adds a font link to the
     host page's head — see W-3 at the top. */
  font-family:"Epilogue",system-ui,sans-serif;
  line-height:1.6;
  text-wrap:pretty;
}
:host([hidden]){display:none}
*{box-sizing:border-box}

.pad{padding:clamp(var(--s-3),4cqi,32px)}
.stack{display:flex;flex-direction:column;align-items:flex-start;gap:var(--s-2)}

h2{margin:0;font-size:var(--fs-2);line-height:1.1;font-weight:var(--w-b);letter-spacing:-.02em;color:var(--ink)}
h3{margin:0;font-size:var(--fs-3);line-height:1.3;font-weight:var(--w-b);color:var(--ink)}
.lead{margin:0;font-size:var(--fs-3);line-height:1.45;color:var(--ink)}

/* Focus is a ring, never a removed outline. Every control gets the same one. */
a:focus-visible,button:focus-visible,input:focus-visible{
  outline:var(--stroke-3) solid var(--teal);outline-offset:var(--stroke-3)
}

.project{display:flex;flex-wrap:wrap;align-items:flex-start;gap:var(--s-2);max-width:520px;text-decoration:none;color:var(--ink)}
.project img{flex:none;width:88px;height:88px;object-fit:cover;object-position:24% 42%;border-radius:var(--radius);background:var(--paper)}
/* The 190px basis is the one number that decides the wrap. Above ~390px the text sits beside the
   image; at 360px and below it drops to a full-width second line on the same left edge. */
.project .words{flex:1 1 190px;display:flex;flex-direction:column;gap:2px;min-width:0}
.project .name{font-size:var(--fs-4);line-height:1.35;font-weight:var(--w-m);color:var(--teal)}
.project .name span{text-decoration:underline;text-decoration-thickness:var(--stroke-1);text-underline-offset:var(--stroke-3)}
.project .desc{font-size:var(--fs-5);line-height:1.4;color:rgba(62,68,84,.78)}
.project-text{font-size:var(--fs-4);font-weight:var(--w-m);color:var(--teal);text-decoration:underline;text-decoration-thickness:var(--stroke-1);text-underline-offset:var(--stroke-3)}

.amounts{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(280px,100%),1fr));gap:var(--s-1);width:100%;margin-top:var(--s-2)}
.amount{
  display:flex;flex-direction:column;align-items:flex-start;gap:2px;
  min-height:72px;padding:12px var(--s-2);text-align:left;
  background:#fff;border:var(--stroke-2) solid rgba(62,68,84,.25);border-radius:var(--radius);
  cursor:pointer;transition:border-color .12s ease;font:inherit
}
.amount:hover{border-color:var(--teal)}
/* Selection is said three ways, never colour alone: fill, weight, and a tick before the
   value. A reader who cannot separate the two colours still sees which one is chosen. */
.amount[aria-checked="true"]{background:var(--teal);border-color:var(--teal)}
.amount .value{font-size:var(--fs-3);font-weight:var(--w-m);line-height:1.2;color:var(--ink)}
.amount[aria-checked="true"] .value{font-weight:var(--w-b);color:var(--paper)}
.amount .sub{font-size:var(--fs-5);line-height:1.35;color:rgba(62,68,84,.75)}
.amount[aria-checked="true"] .sub{color:rgba(244,248,249,.85)}
.amount.other{grid-column:1/-1}

.custom{display:flex;flex-direction:column;gap:var(--s-1);width:100%;max-width:240px}
.custom label{font-size:var(--fs-5);font-weight:var(--w-b);letter-spacing:.06em;text-transform:uppercase;color:var(--teal)}
.custom .box{display:flex;align-items:center;gap:var(--s-1);min-height:var(--control-lg);padding:0 var(--s-2);background:#fff;border:var(--stroke-2) solid var(--teal);border-radius:var(--radius)}
.custom .sign{font-size:var(--fs-3);font-weight:var(--w-b);color:var(--ink)}
/* 52px, not 28px. A short input inside a tall box means the lower half of the field does
   nothing when tapped — a defect found in the handoff's own mobile testing. */
.custom input{flex:1;min-width:0;min-height:52px;padding:0;font:inherit;font-size:var(--fs-3);font-weight:var(--w-b);color:var(--ink);background:transparent;border:0;outline:none}

.cta{
  width:100%;max-width:352px;min-height:var(--control-lg);
  display:inline-flex;align-items:center;justify-content:center;padding:0 var(--s-3);
  font:inherit;font-size:var(--fs-3);font-weight:var(--w-b);text-align:center;
  color:var(--ink);background:var(--lime);border:0;border-radius:var(--radius);cursor:pointer
}
.cta:hover{background:var(--aqua)}
.cta[disabled]{color:rgba(62,68,84,.75);background:#E4F3BE;cursor:progress}

.alert{display:flex;align-items:flex-start;gap:var(--s-1);max-width:520px}
.alert .mark{flex:none;display:flex;align-items:center;justify-content:center;width:var(--s-3);height:var(--s-3);margin-top:2px;border-radius:999px;background:var(--alert);color:#fff;font-size:var(--fs-5);font-weight:var(--w-b);line-height:1}
.alert p{margin:0;font-size:var(--fs-4);line-height:1.45;font-weight:var(--w-m);color:var(--ink)}

/* Readable, not a warning box. It is a true statement, not a disclaimer. */
.accuracy{margin:var(--s-1) 0 0;max-width:520px;font-size:var(--fs-4);line-height:1.5;color:rgba(62,68,84,.78)}
/* The newsletter tick box. A real 20px box, not a styled span — a reader has to be able to
   see at a glance whether it is ticked, and untick it with one tap. */
.consent{display:flex;align-items:flex-start;gap:var(--s-1);max-width:520px;margin-top:var(--s-1);cursor:pointer}
.consent input{flex:none;width:20px;height:20px;margin:2px 0 0;accent-color:var(--teal);cursor:pointer}
.consent span{font-size:var(--fs-5);line-height:1.5;color:rgba(62,68,84,.78)}
.consent a{color:var(--teal)}

/* The price and the VAT. Quiet, but never hidden — a reader is entitled to know the rate
   they are buying at and that the figure on the button is what leaves their account. */
.price{margin:0;font-size:var(--fs-5);line-height:1.5;color:rgba(62,68,84,.75)}

.business{display:flex;flex-wrap:wrap;align-items:center;gap:var(--s-3);background:var(--paper);border-top:var(--stroke-1) solid rgba(62,68,84,.16)}
.business .words{flex:1 1 300px;min-width:0;display:flex;flex-direction:column;gap:var(--s-1)}
.business p{margin:0;max-width:480px;font-size:var(--fs-4);line-height:1.5;color:rgba(62,68,84,.8)}
.business a{
  flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;
  min-height:var(--control);padding:0 var(--s-3);
  font-size:var(--fs-4);font-weight:var(--w-m);color:var(--teal);text-decoration:none;
  background:transparent;border:var(--stroke-2) solid var(--teal);border-radius:var(--radius)
}
.business a:hover{background:var(--teal);color:var(--paper)}

.thanks a{display:inline-flex;align-items:center;min-height:44px;font-size:var(--fs-4);font-weight:var(--w-m);color:var(--teal)}

[hidden]{display:none!important}
`;

/** Read one attribute, or the fallback. Never returns null, so callers never guard. */
function attr(el: Element, name: string, fallback = ''): string {
  const value = el.getAttribute(name);
  return value === null || value === '' ? fallback : value;
}

class FundingCta extends HTMLElement {
  /** Which button is pressed. A KEY, never an amount. */
  private chosen = '';
  /** What the visitor typed in the free box. Sent as typed; the worker decides what it means. */
  private typed = '';
  private busy = false;

  private root!: ShadowRoot;
  private amounts: readonly Amount[] | null = null;
  private amountButtons: HTMLButtonElement[] = [];

  connectedCallback(): void {
    if (this.root !== undefined) return;
    this.root = this.attachShadow({ mode: 'open' });
    this.amounts = readAmounts(this.getAttribute('amounts'));
    // The first amount is pressed to begin with, so the button is never a dead end for
    // somebody who does not want to choose. The handoff preselects £10.
    this.chosen = this.amounts?.[0]?.key ?? '';
    this.draw();
  }

  // ---------------------------------------------------------------------------------------
  // Drawing. All of it happens once, synchronously, before any network request (W-6).
  // ---------------------------------------------------------------------------------------

  private draw(): void {
    const style = document.createElement('style');
    style.textContent = STYLES;
    this.root.append(style, this.drawConsumer(), this.drawThanks(), this.drawBusiness());

    // A visitor coming back from a completed payment. NOT proof of payment — see
    // `cameBackFromPayment` — but the right thing to say to somebody who did pay.
    if (cameBackFromPayment(location.search)) this.showThanks();
  }

  private drawConsumer(): HTMLElement {
    const section = document.createElement('div');
    section.className = 'pad stack';
    section.setAttribute('part', 'consumer');

    const heading = document.createElement('h2');
    heading.textContent = attr(this, 'heading', 'Want to put something back?');

    const lead = document.createElement('p');
    lead.className = 'lead';
    lead.textContent = attr(this, 'supporting', 'Support climate and nature restoration directly.');

    section.append(heading, lead, this.drawProject());

    // No amounts means the block cannot state what it would charge, so it must not offer to
    // charge anything. The business route below still stands on its own, which is why this
    // degrades to a useful block rather than an empty one.
    if (this.amounts !== null) {
      section.append(
        this.drawAmounts(),
        this.drawCustom(),
        this.drawConsent(),
        this.drawCta(),
        this.drawAlert(),
      );
      const price = attr(this, 'price-line');
      if (price !== '') {
        const line = document.createElement('p');
        line.className = 'price';
        line.textContent = price;
        section.append(line);
      }
    }

    const accuracy = document.createElement('p');
    accuracy.className = 'accuracy';
    accuracy.textContent = attr(
      this,
      'accuracy',
      'Funding climate action does not make the original emissions disappear.',
    );
    section.append(accuracy);

    this.consumer = section;
    return section;
  }

  private drawProject(): HTMLElement {
    const name = attr(this, 'project-name', 'this project');
    const place = attr(this, 'project-location');
    const full = place === '' ? name : `${name}, ${place}`;
    const image = attr(this, 'project-image');

    const link = document.createElement('a');
    link.href = attr(this, 'project-url', '#');
    link.setAttribute('data-analytics', 'microfund_project_clicked');

    if (image === '') {
      // Text-only variant. The same sentence, one link, same left edge.
      link.className = 'project-text';
      link.textContent = `Currently supporting ${full} →`;
      return link;
    }

    link.className = 'project';
    const img = document.createElement('img');
    img.src = image;
    img.alt = attr(this, 'project-image-alt', `A photograph from the ${name} project`);
    img.loading = 'lazy';
    img.decoding = 'async';
    // Stated in the markup so the row never collapses while the picture loads. Nothing on
    // the page moves afterwards (W-6).
    img.width = 88;
    img.height = 88;

    const words = document.createElement('span');
    words.className = 'words';
    const line = document.createElement('span');
    line.className = 'name';
    const underlined = document.createElement('span');
    underlined.textContent = `Currently supporting ${full}`;
    line.append(underlined, document.createTextNode(' →'));
    const desc = document.createElement('span');
    desc.className = 'desc';
    desc.textContent = attr(this, 'project-description');
    words.append(line, desc);

    link.append(img, words);
    return link;
  }

  private drawAmounts(): HTMLElement {
    const group = document.createElement('div');
    group.className = 'amounts';
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', 'Contribution amount');

    for (const amount of this.amounts ?? []) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = amount.key === CUSTOM_KEY ? 'amount other' : 'amount';
      button.setAttribute('role', 'radio');
      button.setAttribute('data-analytics', 'microfund_amount_selected');
      button.dataset['key'] = amount.key;

      const value = document.createElement('span');
      value.className = 'value';
      const label = document.createElement('span');
      label.textContent = amount.label;
      value.append(label);

      const sub = document.createElement('span');
      sub.className = 'sub';
      sub.textContent = amount.descriptor;

      button.append(value, sub);
      button.addEventListener('click', () => this.choose(amount.key));
      this.amountButtons.push(button);
      group.append(button);
    }

    // Arrow keys move between radios, which is what a radiogroup promises. Without this the
    // group announces itself as one thing and behaves as another.
    group.addEventListener('keydown', (event) => this.moveWithArrows(event as KeyboardEvent));

    this.group = group;
    this.paintSelection();
    return group;
  }

  private drawCustom(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'custom';
    wrap.hidden = true;

    const id = `${attr(this, 'instance-id', 'cta')}-custom`;
    const label = document.createElement('label');
    label.htmlFor = id;
    // A visible label, not a placeholder. A placeholder disappears the moment somebody types
    // and takes the only explanation of the field with it.
    label.textContent = 'Other amount';

    const box = document.createElement('div');
    box.className = 'box';
    const sign = document.createElement('span');
    sign.className = 'sign';
    sign.setAttribute('aria-hidden', 'true');
    sign.textContent = '£';

    const input = document.createElement('input');
    input.id = id;
    input.type = 'text';
    input.inputMode = 'decimal';
    input.autocomplete = 'off';
    input.setAttribute('aria-label', 'Other amount in pounds');
    input.setAttribute('data-analytics', 'microfund_custom_amount_entered');
    input.addEventListener('input', () => {
      this.typed = input.value;
      this.clearAlert();
    });

    box.append(sign, input);
    wrap.append(label, box);
    this.customWrap = wrap;
    this.customInput = input;
    return wrap;
  }

  /**
   * The newsletter tick box, and the sentence beside it.
   *
   * WHY IT IS HERE AND NOT ON STRIPE'S PAGE. Stripe collects the email address, so this is the
   * last moment C Level controls before the reader gives it. UK marketing rules let us email
   * somebody who has bought from us, but only if they were offered a simple way to refuse
   * **at the moment their details were collected** — not merely in the email afterwards. This
   * box is that offer. Removing it does not just lose a feature; it removes the thing that
   * makes emailing these buyers lawful.
   *
   * It is TICKED to begin with, which is allowed here because they are buying something and
   * the marketing is about our own similar work. It is not allowed for somebody who has not
   * bought, so do not copy this box onto a page that only collects an address.
   *
   * The wording matches `CONSENT_WORDING` in the worker's `aftercare.ts` word for word,
   * because that is the sentence recorded as evidence of what they agreed to.
   */
  private drawConsent(): HTMLElement {
    const id = `${attr(this, 'instance-id', 'cta')}-consent`;

    const label = document.createElement('label');
    label.className = 'consent';
    label.htmlFor = id;

    const box = document.createElement('input');
    box.type = 'checkbox';
    box.id = id;
    box.checked = true;
    box.setAttribute('data-analytics', 'microfund_newsletter_consent');

    const words = document.createElement('span');
    words.append(document.createTextNode(attr(this, 'consent-wording', DEFAULT_CONSENT)));

    // The terms, if the page gave us an address for them. A link the reader can actually read
    // before they agree, rather than after.
    const termsUrl = attr(this, 'terms-url');
    if (termsUrl !== '') {
      words.append(document.createTextNode(' '));
      const link = document.createElement('a');
      link.href = termsUrl;
      // A new tab, so pressing it never loses a half-finished choice of amount.
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Terms and privacy';
      words.append(link, document.createTextNode('.'));
    }

    label.append(box, words);
    this.consentBox = box;
    return label;
  }

  private drawCta(): HTMLElement {
    const holder = document.createElement('div');
    holder.style.width = '100%';
    holder.style.marginTop = '8px';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cta';
    button.setAttribute('data-analytics', 'microfund_checkout_started');
    button.textContent = attr(this, 'cta', 'Fund restoration');
    button.addEventListener('click', () => void this.fund());

    holder.append(button);
    this.cta = button;
    return holder;
  }

  private drawAlert(): HTMLElement {
    const alert = document.createElement('div');
    alert.className = 'alert';
    // `role="alert"` and next to the control that failed — never at the top of the block,
    // where somebody who just pressed the button would not be looking.
    alert.setAttribute('role', 'alert');
    alert.hidden = true;

    const mark = document.createElement('span');
    mark.className = 'mark';
    mark.setAttribute('aria-hidden', 'true');
    mark.textContent = '!';
    const text = document.createElement('p');

    alert.append(mark, text);
    this.alert = alert;
    this.alertText = text;
    return alert;
  }

  private drawThanks(): HTMLElement {
    const section = document.createElement('div');
    section.className = 'pad stack thanks';
    section.setAttribute('role', 'status');
    section.hidden = true;

    const heading = document.createElement('h2');
    const body = document.createElement('p');
    body.className = 'lead';
    body.textContent = attr(this, 'project-description');

    const link = document.createElement('a');
    link.href = attr(this, 'project-url', '#');
    link.textContent = `See ${attr(this, 'project-name', 'the project')} →`;

    const note = document.createElement('p');
    note.className = 'accuracy';

    section.append(heading, body, link, note);
    this.thanks = section;
    this.thanksHeading = heading;
    this.thanksNote = note;
    return section;
  }

  private drawBusiness(): HTMLElement {
    const band = document.createElement('div');
    band.className = 'pad business';
    band.setAttribute('part', 'business');

    const words = document.createElement('div');
    words.className = 'words';
    const heading = document.createElement('h3');
    heading.textContent = attr(this, 'business-heading', 'Doing this for a business?');
    const copy = document.createElement('p');
    copy.textContent = attr(
      this,
      'business-copy',
      'We help organisations measure emissions, choose credible projects and work out what makes sense for their climate strategy.',
    );
    words.append(heading, copy);

    const link = document.createElement('a');
    link.href = attr(this, 'business-url', '/contact/');
    link.setAttribute('data-analytics', 'microfund_business_cta_clicked');
    link.textContent = `${attr(this, 'business-cta', 'Talk to C Level')} →`;

    band.append(words, link);
    return band;
  }

  // ---------------------------------------------------------------------------------------
  // Behaviour.
  // ---------------------------------------------------------------------------------------

  private choose(key: string): void {
    this.chosen = key;
    this.clearAlert();
    this.paintSelection();
    if (key === CUSTOM_KEY && this.customInput !== undefined) {
      this.customInput.focus();
      this.customInput.select();
    }
  }

  private paintSelection(): void {
    for (const button of this.amountButtons) {
      const on = button.dataset['key'] === this.chosen;
      button.setAttribute('aria-checked', on ? 'true' : 'false');
      // Only the pressed radio is in the tab order. Tab reaches the group, arrows move
      // inside it — the behaviour a radiogroup promises.
      button.tabIndex = on ? 0 : -1;
      const label = button.querySelector('.value span');
      if (label !== null) {
        const text = label.textContent ?? '';
        const bare = text.startsWith('✓ ') ? text.slice(2) : text;
        label.textContent = on ? `✓ ${bare}` : bare;
      }
    }
    if (this.customWrap !== undefined) this.customWrap.hidden = this.chosen !== CUSTOM_KEY;
  }

  private moveWithArrows(event: KeyboardEvent): void {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1
      : 0;
    if (step === 0 || this.amountButtons.length === 0) return;
    event.preventDefault();
    const at = this.amountButtons.findIndex((b) => b.dataset['key'] === this.chosen);
    const next = this.amountButtons[(at + step + this.amountButtons.length) % this.amountButtons.length];
    if (next !== undefined) {
      this.choose(next.dataset['key'] ?? '');
      next.focus();
    }
  }

  /**
   * Ask the worker for a payment page, and go there.
   *
   * Nothing here works out an amount. It sends the key of the pressed button and, if that is
   * the free box, the text the visitor typed — unread. The worker turns that into pence.
   */
  private async fund(): Promise<void> {
    if (this.busy || this.cta === undefined) return;
    this.busy = true;
    this.clearAlert();
    this.cta.disabled = true;
    const restLabel = this.cta.textContent ?? 'Fund restoration';
    this.cta.textContent = 'Opening secure checkout…';

    const request = buildDonateRequest(
      attr(this, 'project'),
      this.chosen,
      this.typed,
      sourcePageOf(location.pathname),
      this.consentBox?.checked ?? false,
    );

    let answer;
    try {
      const response = await fetch(`${attr(this, 'endpoint', DEFAULT_ENDPOINT)}/v1/donate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
      answer = readDonateAnswer(await response.json());
    } catch {
      // A dropped connection and a refusal read the same to somebody standing at the button:
      // nothing happened, and no money moved. Say that, and let them press it again.
      answer = { ok: false as const, message: GENERIC_FAILURE };
    }

    if (answer.ok) {
      // Straight to Stripe. The address was checked to be Stripe's own checkout before we
      // got here — see `isStripeCheckoutUrl`. The button stays disabled, because the page is
      // leaving and re-enabling it would invite a second payment.
      location.assign(answer.payUrl);
      return;
    }

    // The pressed amount survives the failure. Nobody has to choose again.
    this.busy = false;
    this.cta.disabled = false;
    this.cta.textContent = restLabel;
    this.showAlert(answer.message);
  }

  private showAlert(message: string): void {
    if (this.alert === undefined || this.alertText === undefined) return;
    this.alertText.textContent = message;
    this.alert.hidden = false;
  }

  private clearAlert(): void {
    if (this.alert !== undefined) this.alert.hidden = true;
  }

  /**
   * The thank-you.
   *
   * It thanks somebody, names the project, and says how many credits they bought — which it
   * may now do, because C Level holds the Plan Vivo stock and a payment genuinely buys some
   * of it (Piers, 23 August 2026).
   *
   * What it still does not do: say the reader's flight is offset, neutral or cancelled out,
   * or print a certificate number. Buying a credit and cancelling out a journey are not the
   * same statement, and we do not issue a certificate on this route.
   */
  private showThanks(): void {
    if (this.consumer === undefined || this.thanks === undefined || this.thanksHeading === undefined) return;
    const amount = amountLabelFromReturn(location.search);
    const credits = creditsLabelFromReturn(location.search);
    const name = attr(this, 'project-name', 'this project');

    this.thanksHeading.textContent = amount === ''
      ? `Thank you. Your contribution supports ${name}.`
      : `Thank you. Your ${amount} buys ${credits === '' ? 'carbon credits' : credits} of Plan Vivo credits at ${name}.`;

    if (this.thanksNote !== undefined) {
      // Said here rather than left to Stripe's email, because a reader who wants a receipt
      // should be told where it is while they are still looking at the page.
      this.thanksNote.textContent =
        'Stripe has emailed your receipt, which shows the VAT you paid. Funding restoration '
        + 'does not make the original emissions disappear.';
    }

    this.consumer.hidden = true;
    this.thanks.hidden = false;
  }

  private consumer?: HTMLElement;
  private thanks?: HTMLElement;
  private thanksHeading?: HTMLElement;
  private thanksNote?: HTMLElement;
  private group?: HTMLElement;
  private customWrap?: HTMLElement;
  private customInput?: HTMLInputElement;
  private cta?: HTMLButtonElement;
  private consentBox?: HTMLInputElement;
  private alert?: HTMLElement;
  private alertText?: HTMLElement;
}

if (!customElements.get('clevel-funding-cta')) {
  customElements.define('clevel-funding-cta', FundingCta);
}

export { FundingCta };
